/**
 * Classes Tab - DataGrid Version with Full CRUD
 *
 * Uses Material-UI DataGrid for enhanced filtering, sorting, and pagination
 * Handles multiple styles per class (ManyToMany relationship)
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  useGetClassesQuery,
  useCreateClassMutation,
  useUpdateClassMutation,
  useDeleteClassMutation,
  useGetStylesQuery,
} from '../../../api/schedulingApi';
import type { Class, ClassFormData, Style } from '../../../types/scheduling';

interface ClassesTabProps {
  scheduleId: string;
  studioId: string;
}

interface ClassFormErrors {
  class_name?: string;
  styles?: string;
  level?: string;
  age_start?: string;
  age_end?: string;
  duration?: string;
  notes?: string;
}

const ClassesTabDataGrid: React.FC<ClassesTabProps> = ({
  scheduleId,
  studioId,
}) => {
  const { data: classes, isLoading } = useGetClassesQuery({
    scheduleId,
    studioId,
  });
  const { data: availableStyles = [] } = useGetStylesQuery({ studioId });
  const [createClass, { isLoading: isCreating }] = useCreateClassMutation();
  const [updateClass, { isLoading: isUpdating }] = useUpdateClassMutation();
  const [deleteClass, { isLoading: isDeleting }] = useDeleteClassMutation();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [deleteDialogClass, setDeleteDialogClass] = useState<Class | null>(
    null
  );

  // Form state
  const [formData, setFormData] = useState<ClassFormData>({
    class_name: '',
    styles: [],
    level: '',
    age_start: 5,
    age_end: 18,
    duration: 60,
    notes: '',
  });

  const [formErrors, setFormErrors] = useState<ClassFormErrors>({});

  // Selected styles for Autocomplete (Style objects)
  const [selectedStyles, setSelectedStyles] = useState<Style[]>([]);

  const resetForm = () => {
    setFormData({
      class_name: '',
      styles: [],
      level: '',
      age_start: 5,
      age_end: 18,
      duration: 60,
      notes: '',
    });
    setSelectedStyles([]);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: ClassFormErrors = {};

    if (!formData.class_name.trim()) {
      errors.class_name = 'Class name is required';
    }
    if (formData.styles.length === 0) {
      errors.styles = 'At least one style is required';
    }
    if (!formData.level.trim()) {
      errors.level = 'Level is required';
    }
    if (formData.age_start < 2 || formData.age_start > 99) {
      errors.age_start = 'Age start must be between 2 and 99';
    }
    if (formData.age_end < formData.age_start || formData.age_end > 99) {
      errors.age_end = 'Age end must be greater than age start and max 99';
    }
    if (formData.duration % 15 !== 0) {
      errors.duration = 'Duration must be a multiple of 15 minutes';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    resetForm();
  };

  const handleOpenEditDialog = useCallback(
    (classItem: Class) => {
      setEditingClass(classItem);

      // Find the Style objects for the selected style IDs
      const selectedStyleObjects = availableStyles.filter(style =>
        classItem.styles.includes(style.id)
      );

      setFormData({
        class_name: classItem.class_name,
        styles: classItem.styles,
        level: classItem.level,
        age_start: classItem.age_start,
        age_end: classItem.age_end,
        duration: classItem.duration * 60, // Convert hours to minutes for form
        notes: classItem.notes || '',
      });
      setSelectedStyles(selectedStyleObjects);
      setFormErrors({});
    },
    [availableStyles]
  );

  const handleCloseEditDialog = () => {
    setEditingClass(null);
    resetForm();
  };

  const handleStylesChange = (newStyles: Style[]) => {
    setSelectedStyles(newStyles);
    setFormData({ ...formData, styles: newStyles.map(s => s.id) });
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      // Convert duration from minutes to hours for backend
      const dataToSend = {
        ...formData,
        duration: formData.duration / 60, // Convert minutes to hours
      };

      await createClass({
        scheduleId,
        studioId,
        data: dataToSend,
      }).unwrap();
      handleCloseAddDialog();
    } catch (error) {
      console.error('Failed to create class:', error);
    }
  };

  const handleUpdate = async () => {
    if (!editingClass || !validateForm()) return;

    try {
      // Convert duration from minutes to hours for backend
      const dataToSend = {
        ...formData,
        duration: formData.duration / 60, // Convert minutes to hours
      };

      await updateClass({
        id: editingClass.id,
        studioId,
        data: dataToSend,
      }).unwrap();
      handleCloseEditDialog();
    } catch (error) {
      console.error('Failed to update class:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialogClass) return;

    try {
      await deleteClass({ id: deleteDialogClass.id, studioId }).unwrap();
      setDeleteDialogClass(null);
    } catch (error) {
      console.error('Failed to delete class:', error);
    }
  };

  // DataGrid columns configuration
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'class_name',
        headerName: 'Class Name',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'styles',
        headerName: 'Styles',
        flex: 1,
        minWidth: 200,
        renderCell: params => {
          const classItem = params.row as Class;
          const styleNames = classItem.style_details
            ? classItem.style_details.map(s => s.title)
            : classItem.styles;

          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
              {styleNames.map(style => (
                <Chip
                  key={`style-${classItem.id}-${style}`}
                  label={style}
                  size="small"
                />
              ))}
            </Box>
          );
        },
      },
      {
        field: 'level',
        headerName: 'Level',
        width: 120,
      },
      {
        field: 'age_range_display',
        headerName: 'Age Range',
        width: 120,
      },
      {
        field: 'duration',
        headerName: 'Duration',
        width: 100,
        valueGetter: (value, row) => Math.round(row.duration * 60),
        renderCell: params => `${params.value} min`,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        getActions: (params: GridRowParams<Class>) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleOpenEditDialog(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => setDeleteDialogClass(params.row)}
            showInMenu
          />,
        ],
      },
    ],
    [handleOpenEditDialog]
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Dance Classes</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Class
        </Button>
      </Stack>

      {!classes || classes.length === 0 ? (
        <Alert severity="info">
          No classes added yet. Click &quot;Add Class&quot; to create your first
          dance class.
        </Alert>
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={classes}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                py: 1,
              },
            }}
          />
        </Paper>
      )}

      {/* Add Class Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Class</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Class Name"
              value={formData.class_name}
              onChange={e =>
                setFormData({ ...formData, class_name: e.target.value })
              }
              error={!!formErrors.class_name}
              helperText={formErrors.class_name}
              required
            />
            <Autocomplete
              multiple
              options={availableStyles}
              getOptionLabel={option => option.title}
              value={selectedStyles}
              onChange={(_, newValue) => handleStylesChange(newValue)}
              renderInput={params => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label="Styles"
                  error={!!formErrors.styles}
                  helperText={formErrors.styles || 'Select one or more styles'}
                  required
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.title}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
            <TextField
              fullWidth
              label="Level"
              value={formData.level}
              onChange={e =>
                setFormData({ ...formData, level: e.target.value })
              }
              error={!!formErrors.level}
              helperText={formErrors.level}
              required
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                type="number"
                label="Age Start"
                value={formData.age_start}
                onChange={e =>
                  setFormData({
                    ...formData,
                    age_start: parseInt(e.target.value, 10) || 5,
                  })
                }
                error={!!formErrors.age_start}
                helperText={formErrors.age_start}
                inputProps={{ min: 2, max: 99 }}
                required
              />
              <TextField
                fullWidth
                type="number"
                label="Age End"
                value={formData.age_end}
                onChange={e =>
                  setFormData({
                    ...formData,
                    age_end: parseInt(e.target.value, 10) || 18,
                  })
                }
                error={!!formErrors.age_end}
                helperText={formErrors.age_end}
                inputProps={{ min: 2, max: 99 }}
                required
              />
            </Stack>
            <TextField
              fullWidth
              type="number"
              label="Duration (minutes)"
              value={formData.duration}
              onChange={e =>
                setFormData({
                  ...formData,
                  duration: parseInt(e.target.value, 10) || 60,
                })
              }
              error={!!formErrors.duration}
              helperText={
                formErrors.duration || 'Must be a multiple of 15 minutes'
              }
              inputProps={{ min: 15, step: 15 }}
              required
            />
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={e =>
                setFormData({ ...formData, notes: e.target.value })
              }
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isCreating}
          >
            Add Class
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Class Dialog */}
      <Dialog
        open={!!editingClass}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Class</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Class Name"
              value={formData.class_name}
              onChange={e =>
                setFormData({ ...formData, class_name: e.target.value })
              }
              error={!!formErrors.class_name}
              helperText={formErrors.class_name}
              required
            />
            <Autocomplete
              multiple
              options={availableStyles}
              getOptionLabel={option => option.title}
              value={selectedStyles}
              onChange={(_, newValue) => handleStylesChange(newValue)}
              renderInput={params => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label="Styles"
                  error={!!formErrors.styles}
                  helperText={formErrors.styles || 'Select one or more styles'}
                  required
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.title}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
            <TextField
              fullWidth
              label="Level"
              value={formData.level}
              onChange={e =>
                setFormData({ ...formData, level: e.target.value })
              }
              error={!!formErrors.level}
              helperText={formErrors.level}
              required
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                type="number"
                label="Age Start"
                value={formData.age_start}
                onChange={e =>
                  setFormData({
                    ...formData,
                    age_start: parseInt(e.target.value, 10) || 5,
                  })
                }
                error={!!formErrors.age_start}
                helperText={formErrors.age_start}
                inputProps={{ min: 2, max: 99 }}
                required
              />
              <TextField
                fullWidth
                type="number"
                label="Age End"
                value={formData.age_end}
                onChange={e =>
                  setFormData({
                    ...formData,
                    age_end: parseInt(e.target.value, 10) || 18,
                  })
                }
                error={!!formErrors.age_end}
                helperText={formErrors.age_end}
                inputProps={{ min: 2, max: 99 }}
                required
              />
            </Stack>
            <TextField
              fullWidth
              type="number"
              label="Duration (minutes)"
              value={formData.duration}
              onChange={e =>
                setFormData({
                  ...formData,
                  duration: parseInt(e.target.value, 10) || 60,
                })
              }
              error={!!formErrors.duration}
              helperText={
                formErrors.duration || 'Must be a multiple of 15 minutes'
              }
              inputProps={{ min: 15, step: 15 }}
              required
            />
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={e =>
                setFormData({ ...formData, notes: e.target.value })
              }
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isUpdating}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteDialogClass}
        onClose={() => setDeleteDialogClass(null)}
      >
        <DialogTitle>Delete Class?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;
            {deleteDialogClass?.class_name}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogClass(null)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassesTabDataGrid;
